import { setup, assign, and, not, enqueueActions, fromPromise, sendTo } from 'xstate';

import { apiCreateOrValidateUser, apiCheckPermissions, apiCheckInstallation, ACTIONS } from '.';

export const PUBLISH_STATES = {
  INIT: 'init',
  STEP_1: 'step1',
  STEP_2: 'step2',
  STEP_3: 'step3',
  INITIAL: 'initial',
  CHECKING_PERMISSIONS: 'checkingPermissions',
  VERIFYING_OR_CREATING_USER: 'verifyingOrCreatingUser',
  NEEDS_VERIFICATION: 'needsVerification',
  NEEDS_APPROVAL: 'needsApproval',
  WAITING_FOR_INSTALLATION: 'waitingForInstallation',
  CHECKING_INSTALLATION: 'checkingInstallation',
};

export const PUBLISH_ACTIONS = {
  SUBMIT_EMAIL: 'submitEmail',
  CLEAR_EMAIL: 'clearEmail',
  START_CHECKING_INSTALLATION: 'startCheckingInstallation',
};

const LS_KEY = 'emailEntered';

const initialContext = (input) => ({
  api: input.api,
  userId: input.userId,
  userEmail: input.userEmail || localStorage.getItem(LS_KEY),
  hasPublishedBefore: input.hasPublishedBefore,
  page: input.page,
  parentRef: input.parentRef,
  polling: false,
  editorId: input.editorId,
});

export const publishMachine = setup({
  delays: {
    installCheckFrequency: 10000,
  },
  actions: {
    saveEmail: enqueueActions(({ enqueue, event }) => {
      enqueue(() => {
        localStorage.setItem(LS_KEY, event.userEmail);
      });
      enqueue.assign({
        userEmail: event.userEmail,
      });
    }),
    clearEmail: enqueueActions(({ enqueue }) => {
      enqueue(() => {
        localStorage.removeItem(LS_KEY);
      });
      enqueue.assign({
        userEmail: null,
      });
    }),
    setPolling: assign({
      polling: true,
    }),
    sendErrorToParent: enqueueActions(({ enqueue, context, event }) => {
      enqueue.sendTo(context.parentRef, {
        type: ACTIONS.REPORT_ERROR,
        title: event.title,
        error: event.error,
      });
      enqueue.sendTo(
        context.parentRef,
        {
          type: ACTIONS.BACK,
        },
        {
          delay: 100,
        },
      );
    }),
    sendMetric: sendTo(
      ({ context }) => context.parentRef,
      (_, params) => ({
        type: ACTIONS.TRACK_METRIC,
        params: {
          name: params.name,
          params: params.params,
        },
      }),
    ),
  },
  actors: {
    apiCreateOrValidateUser: fromPromise(apiCreateOrValidateUser),
    apiCheckPermissions: fromPromise(apiCheckPermissions),
    apiCheckInstallation: fromPromise(apiCheckInstallation),
  },
  guards: {
    hasPublishedBefore: ({ context }) => context.hasPublishedBefore,
    isLoggedIn: ({ context }) => Boolean(context.userId),
    enteredEmail: ({ context }) => Boolean(context.userEmail),
  },
}).createMachine({
  context: ({ input }) => initialContext(input),
  initial: PUBLISH_STATES.STEP_1,
  states: {
    [PUBLISH_STATES.STEP_1]: {
      initial: PUBLISH_STATES.INIT,
      states: {
        [PUBLISH_STATES.INIT]: {
          always: [
            {
              guard: and([not('isLoggedIn'), not('enteredEmail')]),
              target: PUBLISH_STATES.INITIAL,
            },
            {
              guard: and([not('isLoggedIn'), 'enteredEmail']),
              actions: [{ type: 'sendMetric', params: { name: 'email_entered' } }],
              target: PUBLISH_STATES.VERIFYING_OR_CREATING_USER,
            },
            {
              actions: [{ type: 'sendMetric', params: { name: 'email_entered' } }],
              target: PUBLISH_STATES.CHECKING_PERMISSIONS,
            },
          ],
        },
        [PUBLISH_STATES.INITIAL]: {
          on: {
            [PUBLISH_ACTIONS.SUBMIT_EMAIL]: {
              actions: ['saveEmail'],
              target: PUBLISH_STATES.INIT,
            },
          },
        },
        [PUBLISH_STATES.CHECKING_PERMISSIONS]: {
          invoke: {
            src: 'apiCheckPermissions',
            input: ({ context }) => {
              return { api: context.api };
            },
            onDone: [
              {
                guard: ({ event }) => event.output.sentOwnerEmail,
                target: PUBLISH_STATES.NEEDS_APPROVAL,
              },
              {
                target: `#${PUBLISH_STATES.STEP_2}`,
              },
            ],
            onError: {
              actions: [
                {
                  type: 'sendErrorToParent',
                  params: ({ event }) => ({
                    title: 'Failed to verify account.',
                    error: event.error,
                  }),
                },
              ],
            },
          },
        },
        [PUBLISH_STATES.VERIFYING_OR_CREATING_USER]: {
          invoke: {
            src: 'apiCreateOrValidateUser',
            input: ({ context }) => {
              return { api: context.api, userEmail: context.userEmail, editorId: context.editorId };
            },
            onDone: [
              {
                guard: ({ event }) => event.output.sentOwnerEmail,
                target: PUBLISH_STATES.NEEDS_APPROVAL,
              },
              {
                guard: ({ event }) => event.output.sentUserValidationEmail,
                target: PUBLISH_STATES.NEEDS_VERIFICATION,
              },
              {
                actions: [
                  sendTo(
                    ({ context }) => context.parentRef,
                    ({ event }) => ({
                      type: ACTIONS.SET_NEW_USER,
                      output: {
                        userId: event.output.userId,
                        userEmail: event.output.userEmail,
                        hasPublishPermissions: event.output.publishAccess,
                        capabilities: event.output.capabilities,
                      },
                    }),
                  ),
                ],
                target: `#${PUBLISH_STATES.STEP_2}`,
              },
            ],
            onError: {
              actions: [
                {
                  type: 'sendErrorToParent',
                  params: ({ event }) => ({
                    title: 'Failed to verify account.',
                    error: event.error,
                  }),
                },
              ],
            },
          },
        },
        [PUBLISH_STATES.NEEDS_VERIFICATION]: {
          on: {
            [PUBLISH_ACTIONS.CLEAR_EMAIL]: {
              actions: ['clearEmail'],
              target: PUBLISH_STATES.INITIAL,
            },
          },
        },
        [PUBLISH_STATES.NEEDS_APPROVAL]: {},
      },
    },
    [PUBLISH_STATES.STEP_2]: {
      id: PUBLISH_STATES.STEP_2,
      initial: PUBLISH_STATES.CHECKING_INSTALLATION,
      entry: [{ type: 'sendMetric', params: { name: 'email_verified' } }],
      states: {
        [PUBLISH_STATES.INITIAL]: {
          entry: [{ type: 'sendMetric', params: { name: 'install_viewed' } }],
          on: {
            [PUBLISH_ACTIONS.START_CHECKING_INSTALLATION]: {
              actions: ['setPolling', { type: 'sendMetric', params: { name: 'install_copied' } }],
              target: PUBLISH_STATES.CHECKING_INSTALLATION,
            },
          },
        },
        [PUBLISH_STATES.WAITING_FOR_INSTALLATION]: {
          after: {
            installCheckFrequency: {
              target: PUBLISH_STATES.CHECKING_INSTALLATION,
            },
          },
        },
        [PUBLISH_STATES.CHECKING_INSTALLATION]: {
          invoke: {
            src: 'apiCheckInstallation',
            input: ({ context }) => {
              return { api: context.api };
            },
            onDone: [
              {
                guard: ({ event }) => event.output === true,
                actions: [{ type: 'sendMetric', params: { name: 'install_verified' } }],
                target: `#${PUBLISH_STATES.STEP_3}`,
              },
              {
                guard: ({ context }) => context.polling,
                target: PUBLISH_STATES.WAITING_FOR_INSTALLATION,
              },
              {
                target: PUBLISH_STATES.INITIAL,
              },
            ],
            onError: {
              actions: [
                {
                  type: 'sendErrorToParent',
                  params: ({ event }) => ({
                    title: 'Failed to check script installation.',
                    error: event.error,
                  }),
                },
              ],
            },
          },
        },
      },
    },
    [PUBLISH_STATES.STEP_3]: {
      id: PUBLISH_STATES.STEP_3,
    },
  },
});
